export const ROLES = {
  OWNER: 'Owner',
  SUPER_ADMIN: 'SuperAdmin',
  ADMIN: 'Admin',
  WRITE: 'Write',
  READ: 'Read',
};

/**
 * The signed-in user's email, from the localStorage copy written at login.
 * userData[3] is the email column; see getCurrentUser below.
 */
export function getUserEmail() {
  return getCurrentUser().email;
}

/**
 * Appends the `email_id` query parameter every /mcv API now requires.
 *
 *   fetch(withEmailId(`/mcv/history_metadata?media_type=${t}`), { ... })
 *
 * Picks ? or & depending on what the URL already has, and keeps any #fragment at the
 * end where it belongs. When no email is cached the parameter is still sent, empty:
 * the backend only requires it to be present, and falls back to the identity in the
 * signed cookie, so a cleared localStorage degrades instead of 422-ing every screen.
 */
export function withEmailId(url) {
  const [base, ...rest] = String(url).split('#');
  const separator = base.includes('?') ? '&' : '?';
  const withParam = `${base}${separator}email_id=${encodeURIComponent(getUserEmail() || '')}`;
  return rest.length ? `${withParam}#${rest.join('#')}` : withParam;
}

export function getCurrentUser() {
  let userData = null;
  try {
    userData = JSON.parse(localStorage.getItem('userData'));
  } catch (err) {
    userData = null;
  }
  if (!Array.isArray(userData)) {
    return {
      isSignedIn: false, email: '', role: null,
      isCreator: false, isApprover: false, isSuperAdmin: false,
      seesAllDocuments: false,
      canCreate: false, canApprove: false,
      canUseCreatives: false, canUseApprovals: false,
    };
  }

  const role = userData[6] || null;
  const isCreator = Boolean(userData[7]);
  const isApprover = Boolean(userData[8]);
  const isSuperAdmin = role === ROLES.SUPER_ADMIN || role === ROLES.OWNER;
  const seesAllDocuments = role === ROLES.OWNER;

  return {
    isSignedIn: true,
    email: userData[3] || '',
    role,
    isCreator,
    isApprover,
    isSuperAdmin,
    seesAllDocuments,
    canCreate: isCreator,
    canApprove: isApprover,
    canUseCreatives: !(isApprover && !isCreator),
    canUseApprovals: !(isCreator && !isApprover),
  };
}

/**
 * Where a user lands after signing in.
 *
 *   assigned Approver only  -> the approval queue they have to clear
 *   assigned Creator        -> their creatives (also when assigned both: that is
 *                              where work starts, and the queue is one click away)
 *   assigned neither        -> the Dashboard, which is what everyone had before
 */
export function landingRoute() {
  const { isCreator, isApprover } = getCurrentUser();
  if (isCreator) return '/mcv/creatives';
  if (isApprover) return '/mcv/approval-dashboard';
  return '/mcv';
}
